import { useState, useContext, useEffect } from "react";
import Modal from "../Components/Modal";
import LoadingButton from "../Components/LoadingButton";
import CuisineFieldset from "../Components/CuisineFieldset";
import { ToastContext } from "../Components/ToastProvider";
import { useAuth } from "../hooks/useAuth";
import { useCuisines } from "../hooks/useData";

function RadioChip({ name, value, label, checked, onChange }) {
  return (
    <label className={`chip ${checked ? "chip--selected" : ""}`}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
      />
      {label}
    </label>
  );
}

export default function CompleteProfileModal({
  isOpen,
  toggleOpen,
  onSave,
  showErrorMessage,
}) {
  const { createToast } = useContext(ToastContext);
  const { profile, updateUser } = useAuth();
  const { cuisines: cuisineChoices } = useCuisines();

  const [disabled, setDisabled] = useState(false);
  const [favoriteFood, setFavoriteFood] = useState("");
  const [askMeAbout, setAskMeAbout] = useState("");
  const [gender, setGender] = useState("");
  const [spiceLevel, setSpiceLevel] = useState("");
  const [age, setAge] = useState("");
  const [cuisines, setCuisines] = useState([]);

  const maxChars = 150;

  useEffect(() => {
    if (!profile) return;
    if (profile.favorite_food) setFavoriteFood(profile.favorite_food);
    if (profile.ask_me_about) setAskMeAbout(profile.ask_me_about);
    if (profile.gender) setGender(profile.gender);
    if (profile.spice_level) setSpiceLevel(profile.spice_level);
    if (profile.age_range) setAge(profile.age_range);
    if (profile.cuisines?.length)
      setCuisines(profile.cuisines.map((c) => c.id));
  }, [profile]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (cuisines.length === 0) {
      createToast("Please select at least one cuisine.", "error");
      return;
    }
    setDisabled(true);
    updateUser({
      favoriteFood,
      gender,
      spiceLevel,
      askMeAbout,
      age,
      cuisineIds: cuisines,
    }).then(async (success) => {
      if (success) {
        if (!onSave)
          createToast("Profile updated successfully 🚀", "success", true);
        setDisabled(false);
        const canClose = onSave
          ? onSave({
              commandFromCompleteProfileModal: true,
              updatedProfile: {
                favoriteFood,
                gender,
                spiceLevel,
                askMeAbout,
                age,
                cuisineIds: cuisines,
              },
            })
          : true;
        if (canClose !== false) toggleOpen(false);
      } else {
        createToast("Please try again", "error", true);
        setDisabled(false);
      }
    });
  };

  const genderOptions = [
    { value: "Woman", label: "Woman" },
    { value: "Man", label: "Man" },
    { value: "Non-Binary", label: "Non-binary" },
    { value: "Rather not say", label: "Rather not say" },
  ];

  const ageOptions = ["20s", "30s", "40s", "50s", "60s", "70s", "80+"];

  const spiceOptions = [
    { value: "🌿 Low", label: "🌿 Low" },
    { value: "🌶️ Medium", label: "🌶️ Medium" },
    { value: "🔥 High", label: "🔥 High" },
  ];

  return (
    <Modal
      title="Just one last thing..."
      description="Help fellow Fudopians get to know you before you sit down together. ✨"
      isOpen={isOpen}
      handleDismiss={() => toggleOpen(!isOpen)}
    >
      <form onSubmit={handleSubmit}>
        <div className="field">
          <label className="field-label">
            Gender <span className="required-field">*</span>
          </label>
          <div className="chips">
            {genderOptions.map((opt) => (
              <RadioChip
                key={opt.value}
                name="gender"
                value={opt.value}
                label={opt.label}
                checked={gender === opt.value}
                onChange={(e) => setGender(e.target.value)}
              />
            ))}
          </div>
        </div>

        <div className="field-divider" />

        <div className="field">
          <label className="field-label">
            Age Range <span className="required-field">*</span>
          </label>
          <div className="chips">
            {ageOptions.map((opt) => (
              <RadioChip
                key={opt}
                name="age"
                value={opt}
                label={opt}
                checked={age === opt}
                onChange={(e) => setAge(e.target.value)}
              />
            ))}
          </div>
        </div>

        <div className="field-divider" />

        <div className="field">
          <label className="field-label" htmlFor="favoriteFood">
            Favourite Dish of All Time <span className="required-field">*</span>
          </label>
          <input
            id="favoriteFood"
            type="text"
            value={favoriteFood}
            required
            placeholder="e.g. Pad Kra Pao, my mum's lasagna..."
            onChange={(e) => setFavoriteFood(e.target.value)}
          />
        </div>

        <div className="field-divider" />

        <div className="field">
          <label className="field-label">
            How Spicy Do You Like It? <span className="required-field">*</span>
          </label>
          <div className="chips">
            {spiceOptions.map((opt) => (
              <RadioChip
                key={opt.value}
                name="spiceLevel"
                value={opt.value}
                label={opt.label}
                checked={spiceLevel === opt.value}
                onChange={(e) => setSpiceLevel(e.target.value)}
              />
            ))}
          </div>
        </div>

        <div className="field-divider" />

        <div className="field">
          <CuisineFieldset
            cuisines={cuisines}
            setCuisines={setCuisines}
            cuisineChoices={cuisineChoices}
          />
        </div>

        <div className="field-divider" />

        <div className="field">
          <label className="field-label" htmlFor="askMeAbout">
            Ask Me About… <span className="required-field">*</span>
          </label>
          <p className="field-hint">
            Food, hobbies, anything that makes you interesting to sit next to.
          </p>
          <input
            id="askMeAbout"
            type="text"
            value={askMeAbout}
            required
            maxLength={maxChars}
            placeholder="e.g. natural wine, ceramics, Northern Thai street food..."
            onChange={(e) => setAskMeAbout(e.target.value)}
          />
          <p className="remark">
            {askMeAbout.length} / {maxChars}
          </p>
        </div>

        <div className="field-divider" />

        {showErrorMessage && (
          <p className="error-message">
            Almost there! Please fill out the fields marked with * before
            joining.
          </p>
        )}

        <div className="btn-inline">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => toggleOpen(!isOpen)}
          >
            Maybe later
          </button>
          <LoadingButton
            type="submit"
            color="primary"
            variant="contained"
            disabled={disabled}
          >
            Continue
          </LoadingButton>
        </div>
      </form>
    </Modal>
  );
}
